import { useEffect, useMemo, useState } from "react";
import adminStyles from "../ui/AdminStyles";
import { authedPost } from "../api/customerAuthedFetch";

function ImportStatusBadge({ status }) {
  const map = {
    OK: { bg: "#dcfce7", fg: "#166534" },
    WARNING: { bg: "#fef3c7", fg: "#92400e" },
    ERROR: { bg: "#fee2e2", fg: "#991b1b" },
  };
  const c = map[status] || { bg: "#e5e7eb", fg: "#374151" };
  return (
    <span
      style={{
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        background: c.bg,
        color: c.fg,
        whiteSpace: "nowrap",
      }}
    >
      {status}
    </span>
  );
}

export default function ImportUsersModal({
  open,
  customerDisplayName,
  onClose,
  onSuccess,
}) {
  const [csvText, setCsvText] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [commitLoading, setCommitLoading] = useState(false);
  const [commitError, setCommitError] = useState(null);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [overwriteMode, setOverwriteMode] = useState("skip_existing");

  useEffect(() => {
    if (!open) return;
    setCsvText("");
    setPreview(null);
    setPreviewError(null);
    setConfirmChecked(false);
    setOverwriteMode("skip_existing");
    setCommitError(null);
  }, [open]);

  const hasErrors = useMemo(
    () => (preview?.items || []).some((r) => r.status === "ERROR"),
    [preview]
  );
  const anyExisting = useMemo(
    () => (preview?.items || []).some((r) => !!r.email_exists),
    [preview]
  );

  async function callPreview(text) {
    setPreviewLoading(true);
    setPreviewError(null);
    setPreview(null);
    setConfirmChecked(false);
    try {
      const data = await authedPost("/customer/users/import/preview", {
        csv_text: text,
        customer_display_name: customerDisplayName || "",
      });
      setPreview(data);
    } catch (e) {
      setPreviewError(e?.message || "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  }

  async function callCommit() {
    if (!preview) return;
    setCommitLoading(true);
    setCommitError(null);
    try {
      const data = await authedPost("/customer/users/import/commit", {
        csv_text: csvText,
        customer_display_name: customerDisplayName || "",
        overwrite_mode: overwriteMode,
        default_customer_roles: ["Viewer"],
        send_invite: true,
      });
      if ((data?.counts?.error || 0) === 0) {
        onSuccess?.(data);
      } else {
        setCommitError("Import completed with errors. Review the result and retry.");
      }
    } catch (e) {
      setCommitError(e?.message || "Commit failed");
    } finally {
      setCommitLoading(false);
    }
  }

  if (!open) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        style={{ width: 1100, maxWidth: "95vw" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h3 style={{ margin: 0 }}>Import Users (CSV)</h3>
          <button onClick={onClose}>✕</button>
        </div>
        <hr />
        <p style={{ fontSize: 13, color: "#6b7280" }}>
          Columns: email, name, optional username. Header row is optional.
        </p>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <input
            type="file"
            accept=".csv,.txt"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              setPreview(null);
              setPreviewError(null);
              setConfirmChecked(false);
              if (!f) return setCsvText("");
              setCsvText(await f.text());
            }}
          />
          <button
            disabled={!csvText.trim() || previewLoading}
            onClick={() => callPreview(csvText)}
          >
            {previewLoading ? "Previewing…" : "Preview Import"}
          </button>
          <div style={{ marginLeft: "auto" }}>
            <select
              value={overwriteMode}
              onChange={(e) => setOverwriteMode(e.target.value)}
            >
              <option value="skip_existing">Skip existing users (safe)</option>
              <option value="error_on_existing">
                Error if any email already exists
              </option>
            </select>
          </div>
        </div>

        {previewError && (
          <div style={{ marginTop: 12, color: "#991b1b" }}>{previewError}</div>
        )}

        {preview && (
          <div style={{ marginTop: 16, maxHeight: 320, overflow: "auto" }}>
            <table style={adminStyles.table}>
              <thead>
                <tr>
                  <th style={adminStyles.th}>Email</th>
                  <th style={adminStyles.th}>Final Username</th>
                  <th style={adminStyles.th}>Status</th>
                  <th style={adminStyles.th}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {(preview.items || []).map((r) => (
                  <tr key={r.row}>
                    <td style={adminStyles.td}>{r.email}</td>
                    <td style={adminStyles.td}>{r.final_username}</td>
                    <td style={adminStyles.td}>
                      <ImportStatusBadge status={r.status} />
                    </td>
                    <td style={adminStyles.td}>
                      {(r.errors || r.warnings || []).join(" • ") || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {preview && (
          <div style={{ marginTop: 16, display: "flex", alignItems: "center" }}>
            <label style={{ fontSize: 13 }}>
              <input
                type="checkbox"
                checked={confirmChecked}
                onChange={(e) => setConfirmChecked(e.target.checked)}
              />{" "}
              I reviewed the preview and confirm import
            </label>
            <button
              disabled={
                commitLoading ||
                !confirmChecked ||
                hasErrors ||
                (overwriteMode === "error_on_existing" && anyExisting)
              }
              onClick={callCommit}
              style={{ marginLeft: "auto" }}
            >
              {commitLoading ? "Importing…" : "Commit Import"}
            </button>
          </div>
        )}

        {commitError && (
          <div style={{ marginTop: 12, color: "#991b1b" }}>{commitError}</div>
        )}
      </div>
    </div>
  );
}
